import React, { useState } from 'react';
import { Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import Logo from '../common/Logo';

const AdminLogin = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Simulate loading for better UX
    setTimeout(() => {
      // Check credentials (stored in localStorage or hardcoded)
      const storedUsername = localStorage.getItem('admin_username') || 'admin';
      const storedPassword = localStorage.getItem('admin_password') || 'admin123';

      if (credentials.username === storedUsername && credentials.password === storedPassword) {
        // Generate session token
        const token = btoa(`${credentials.username}:${Date.now()}`);
        localStorage.setItem('admin_token', token);
        localStorage.setItem('admin_login_time', Date.now().toString());
        onLogin();
      } else {
        setError('Invalid username or password');
        setLoading(false);
      }
    }, 800);
  };

  const handleChange = (e) => {
    setCredentials(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setError('');
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6 relative overflow-hidden">
      {/* Ambient glows — matches storefront hero */}
      <div className="absolute top-1/4 -right-32 w-[500px] h-[500px] bg-orange-500/10 rounded-full blur-[130px] pointer-events-none" />
      <div className="absolute bottom-1/4 -left-32 w-[450px] h-[450px] bg-blue-600/10 rounded-full blur-[110px] pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-purple-600/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="inline-block mb-5">
            <Logo className="h-14 text-white" />
          </div>
          <p className="text-orange-500 text-[11px] font-bold tracking-[0.25em] uppercase mb-3">
            Admin Panel
          </p>
          <h1 className="text-3xl font-bold text-white tracking-tight">Welcome back</h1>
          <p className="text-gray-400 text-sm mt-2">Sign in to manage your store</p>
        </div>

        {/* Login Card */}
        <div className="bg-white/[0.06] backdrop-blur-2xl rounded-3xl shadow-[0_24px_60px_-12px_rgba(0,0,0,0.8)] p-8 border border-white/10">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Username Field */}
            <div>
              <label className="block text-[13px] font-semibold text-gray-300 mb-2">
                Username
              </label>
              <input
                type="text"
                name="username"
                value={credentials.username}
                onChange={handleChange}
                required
                className="w-full px-4 py-3.5 bg-white/[0.07] border border-white/10 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500/70 focus:border-transparent transition-all"
                placeholder="Enter your username"
                autoComplete="username"
              />
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-[13px] font-semibold text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={credentials.password}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3.5 bg-white/[0.07] border border-white/10 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500/70 focus:border-transparent transition-all pr-12"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" strokeWidth={1.75} />
                  ) : (
                    <Eye className="w-5 h-5" strokeWidth={1.75} />
                  )}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-3.5 text-red-300 text-sm text-center">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2.5 px-6 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-2xl transition-all duration-300 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_12px_28px_-8px_rgba(249,115,22,0.5)]"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <Lock className="w-[18px] h-[18px]" strokeWidth={2} />
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>

          {/* Back to Store Link */}
          <div className="mt-7 text-center">
            <a
              href="/"
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
              Back to Store
            </a>
          </div>
        </div>

        <p className="text-center text-[11px] tracking-[0.25em] uppercase text-white/20 font-medium mt-8">
          Premium Retail &amp; Wholesale
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
